import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiHeart, FiUsers, FiAward } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiTarget,
      title: 'Our Mission',
      description: 'To provide a premium fitness experience in a welcoming, family-oriented environment where everyone can achieve their health and wellness goals.'
    },
    {
      icon: FiHeart,
      title: 'Our Passion',
      description: 'We are passionate about fitness and creating a community where members feel comfortable, motivated, and supported in their fitness journey.'
    },
    {
      icon: FiUsers,
      title: 'Our Community',
      description: 'A diverse, friendly community free from intimidation or showing off. We believe fitness should be enjoyable and accessible to everyone.'
    },
    {
      icon: FiAward,
      title: 'Our Excellence',
      description: 'State-of-the-art equipment, expert guidance, and continuous innovation to provide the best possible fitness experience.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About <span className="text-yellow-500">Body Fitness Cers</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              More than just a gym - we're a community dedicated to helping you achieve 
              your fitness goals in a supportive, premium environment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 text-white">
                Our <span className="text-yellow-500">Story</span>
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Founded with a vision to create a different kind of fitness experience, 
                  Body Fitness Cers has been serving our community for over 8 years. We believe 
                  that fitness should be accessible, enjoyable, and free from intimidation.
                </p>
                <p>
                  Our state-of-the-art facility features the latest equipment, but what truly 
                  sets us apart is our welcoming atmosphere. Here, you won't find heavy-handed 
                  people trying to show off or create an uncomfortable environment.
                </p>
                <p>
                  We've built a small family club where members can practice cardio and weight 
                  training with pleasure, surrounded by adorable people who genuinely care about 
                  your fitness journey.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop&crop=center"
                alt="Body Fitness Cers Interior"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Our <span className="text-yellow-500">Values</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-yellow-500/10 to-transparent p-8 rounded-2xl border border-yellow-500/20 hover:border-yellow-500/50 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={value.icon} className="text-black text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-yellow-500">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
              Meet Our Team
            </h2>
            <p className="text-xl text-black/80 mb-12 max-w-2xl mx-auto">
              Our dynamic and friendly team is here to support you every step of the way
            </p>
            
            <div className="bg-black/10 p-8 rounded-2xl max-w-2xl mx-auto">
              <p className="text-lg text-black/90 italic">
                "Our team consists of certified trainers, nutritionists, and wellness experts 
                who are passionate about helping you achieve your goals. We pride ourselves on 
                creating a supportive environment where everyone feels welcome."
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;